import { useState, useEffect, FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { IDiaCortesiaDetalle } from 'gateways/notificaciones.interfaces';
import Term from 'components/term';
import { dateFormat } from 'utils/dates';
import moment from 'moment';

const Frame = styled.div<IFrameProps>`
  width: 270px;
  border: 1px solid lightgrey;
  box-shadow: 2px 2px 2px #eee;

  ${(props: IFrameProps) =>
    props.isSmall &&
    css`
        width: 250px;
      `}
`;

const Header = styled.div<IHeaderProps>`
  font-size: 18px;
  font-weight: bold;
  padding: 10px 10px 5px 10px;
  display: block;
  justify-content: space-between;
  background-color: #f5f6fa;
  text-align: center;

  ${(props: IHeaderProps) =>
    props.isSmall &&
    css`
        font-size: 15px;
      `}
`;

const Body = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
`;

const Day = styled.div<IDayProps>`
  width: 13.5%;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  ${(props: IDayProps) =>
    props.isSmall &&
    css`
        font-size: 0.9rem;
        `}

    ${(props: IDayProps) =>
    props.isToday &&
    css`
        border: 1px solid #eee;
        `}

    ${(props: IDayProps) =>
    props.isSelected &&
    css`
        background-color: #2196F3;
        color: white;
        `}

    ${(props: IDayProps) =>
    props.isSelectedNoModificable &&
    css`
            background-color: #DE942B;
            color: white;
        `}

    ${(props: IDayProps) =>
    props.isDisabled &&
    css`
            pointer-events: none;
            background-color: #dddddd;
        `}
`;

interface IFrameProps {
  isSmall?: boolean;
}

interface IHeaderProps {
  isSmall?: boolean;
}

interface IDayProps {
  isToday?: boolean;
  isSelected?: boolean;
  isSelectedNoModificable?: boolean;
  isDisabled?: boolean;
  isSmall?: boolean;
}

export type ICalendarProps = {
  firstDate: Date;
  diasCortesia: IDiaCortesiaDetalle[]; // días de cortesia 
  fechaPorDefecto?: string; // día activo por defecto si no tiene activos
  maxDiasSeleccionables?: number; // por defecto 1
  isSmall?: boolean;
  isDisabledOnClick?: boolean, 
  setDiaSeleccionado?: (fecha: string) => void
}

interface IDiasSeleccionados {
  item: any;
  diaSeleccionado: IDiaCortesiaDetalle;
}

const MonthCalendar: FC<ICalendarProps> = ({ firstDate, diasCortesia, setDiaSeleccionado, maxDiasSeleccionables = 1, fechaPorDefecto, isDisabledOnClick, isSmall }) => {
  const DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const DAYS_LEAP = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const DAYS_OF_THE_WEEK = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];
  const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const today = new Date();

  // Local states    
  // const date: Date = useMemo(() => (new Date(firstDate)) ,[firstDate])
  // console.log('------------------', firstDate);
  // console.log('date', date);
  const [, setDay] = useState<number | undefined>(undefined)
  const [month, setMonth] = useState<number | undefined>(undefined);
  // console.log('month', month);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [startDay, setStartDay] = useState<number | undefined>(undefined);//0-6 0 domingo 6 sabado
  // console.log('(startDay - 1): ', startDay ? startDay - 1 : ' -');

  

  function getStartDayOfMonth(fecha: Date) {
    return new Date(fecha.getFullYear(), fecha.getMonth(), 1).getDay();
  }

  function isLeapYear(anyo: number) {
    return (anyo % 4 === 0 && anyo % 100 !== 0) || anyo % 400 === 0;
  }

  const days = year && isLeapYear(year) ? DAYS_LEAP : DAYS;


  const selectDay = (fecha: string | undefined) => {
    //console.log('selectDay ', fecha)
    if(isDisabledOnClick === true ||!setDiaSeleccionado){return;}
    if(!fecha){return;}

    const diaSeleccionado = diasCortesia.find(dia => dia.fecha === fecha);
    const currActive = diasCortesia.filter(d => d.seleccionada && d.modificable);
    
    if( !diaSeleccionado
        ||!maxDiasSeleccionables
        // ||( currActive.map( d => d.fecha).includes(diaSeleccionado.fecha) ) // No permite desseleccionar fecha seleccionada
    ){ // No hacer ninguna acción
      return;
    }

    setDiaSeleccionado(fecha)
  }


  useEffect(() => {
    // Si hay fecha por defecto y está comprendida en el MES que renderizamos
    if(fechaPorDefecto && new Date(fechaPorDefecto).getMonth() === month){
      const activeItems = diasCortesia.filter(d => d.seleccionada && d.modificable)
      //console.log('activeItems ', activeItems)
      // Si no hay fechas activas seteamos la de por defecto
      if(fechaPorDefecto && activeItems.length === 0 && setDiaSeleccionado ){
        //console.log('inicialidar dia seleccionado fPorDefecto')
        setDiaSeleccionado(fechaPorDefecto)
      }
    }
  },[fechaPorDefecto, month])
  
  useEffect(() => {
    //console.log('Effect calendar', firstDate)
    if(firstDate){
      const date = new Date(firstDate)
      setDay(date.getDate() );
      setMonth(date.getMonth());
      setYear(date.getFullYear());
      setStartDay(getStartDayOfMonth(date));
    }
  }, [firstDate]);

  

  return (
    month && year && startDay ?
    <Frame isSmall={isSmall}>
      <Header isSmall={isSmall}>
        <div>
          <Term component="Global" text={MONTHS[month]} />&nbsp;{year}
        </div>
      </Header>
      <Body>
        {DAYS_OF_THE_WEEK.map(d => (
          <Day key={d}>
            <strong style={isSmall ? { fontSize: '0.9rem' } : {}}><Term component="Global" text={d} /></strong>
          </Day>
        ))}
        {
          Array(days[month] + (startDay !== 0 ? startDay - 1 : 6))
            .fill(null)
            .map((_, index) => {
              const d = index - ((startDay === 0 ? 7 : startDay) - 2);
              const currentDate = new Date(year, month, d);
              const diaCortesia = diasCortesia.find(dia => {
                const newDay = new Date(dia.fecha)
                return d > 0
                  && newDay.getFullYear() === currentDate.getFullYear()
                  && newDay.getMonth() === currentDate.getMonth()
                  && newDay.getDate() === currentDate.getDate()
              });
              const isSelected = diaCortesia && diaCortesia.seleccionada && diaCortesia.modificable;
              const isSelectedNoModificable = diaCortesia && diaCortesia.seleccionada && !diaCortesia.modificable;
              const isDisabled = diaCortesia && ((currentDate < today && !isSelectedNoModificable) || !diaCortesia.modificable);

              return (
                <Day
                  id={`calendar_day_${index}_${dateFormat(currentDate)}`}
                  key={index}
                  isSelected={isSelected}
                  isSelectedNoModificable={isSelectedNoModificable}
                  isDisabled={isDisabled && !isSelectedNoModificable}
                  isSmall={isSmall}
                  onClick={() => selectDay(diaCortesia?.fecha ?? undefined)}
                >
                  {d > 0 ? d : ''}
                </Day>
              );
            })
        }
      </Body>
    </Frame>
    : <></>
  );
}

export default MonthCalendar;